import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { get } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import { ProfileSummary } from "../helper/interface";

interface ProfileContextType {
  profile: ProfileSummary | null;
  avatarUrl: string | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const avatarObjectUrlRef = useRef<string | null>(null);

  const revokeAvatarUrl = () => {
    if (!avatarObjectUrlRef.current) {
      return;
    }

    URL.revokeObjectURL(avatarObjectUrlRef.current);
    avatarObjectUrlRef.current = null;
  };

  const loadProfile = async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    if (!silent) {
      setLoading(true);
    }

    try {
      const profileResponse = await get(Routes.GET_PROFILE);
      setProfile(profileResponse);

      revokeAvatarUrl();
      setAvatarUrl(null);

      if (profileResponse?.hasProfilePhoto) {
        try {
          const photoBlob = await get(Routes.PROFILE_PHOTO, undefined, undefined, "blob");
          if (photoBlob instanceof Blob && photoBlob.size > 0) {
            const nextUrl = URL.createObjectURL(photoBlob);
            avatarObjectUrlRef.current = nextUrl;
            setAvatarUrl(nextUrl);
          }
        } catch (error) {
          console.error("Failed to load profile photo:", error);
        }
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
      revokeAvatarUrl();
      setAvatarUrl(null);
      setProfile(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadProfile();

    return () => {
      revokeAvatarUrl();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        avatarUrl,
        loading,
        refreshProfile: () => loadProfile({ silent: true }),
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useProfile = () => {
  const context = useContext(ProfileContext);

  if (!context) {
    throw new Error("useProfile must be used within ProfileProvider");
  }

  return context;
};
